import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Box, Grid, Modal } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { convertToFormData, getSelectedRoleIds, updateArrayState, validateSeries } from '../utils';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import service from '../../../apis/service';
import { showProgress } from '../../../redux/actions/DealershipActions';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import SeriesInfo from './SeriesInfo';
import useFetchData from '../hooks/useFetchData';
import Steps from '../../common/Steps';
import RoleLevelConfig from './RoleLevelConfig';
import useFetchDepartments from '../hooks/useFetchDepartments';
import ShowProgress from '../../ui/Backdrop';
import CustomPopupFooter from '../../shared/CustomPopupFooter';
import CustomPopupTopBar from '../../shared/CustomPopupTopBar';

const modalStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '1054px',
  height: '80vh',
  backgroundColor: '#F2F4F7',
  boxShadow: 24,
};

const useStyle = makeStyles({
  headerConatiner: {
    width: '100%',
    height: '72px',
    padding: '24px 16px',
    background: 'white',
  },
  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 16px 0px 16px',
    backgroundColor: '#F2F4F7',
  },
  buttonContainer: {
    background: '#FFFFFF',
    height: '64px',
    borderTop: '1px solid #E0E0E0',
    padding: '0 16px',
  },
});

const AddSeries = ({ seriesId = null, close: handleClose, isOpen }) => {
  const dispatch = useDispatch();
  const classes = useStyle();
  const rawPlanList = useSelector((state) => state.GetPlanList.planlist);
  const planList = useMemo(() => rawPlanList?.map((i) => ({ value: i.id, label: i.title })) || [], [rawPlanList]);
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    plans: [],
    departmentRoleIds: [],
    visibility: ['DEALERSHIP'],
    startDate: moment().add(1, 'days').format('YYYY-MM-DD'),
    endDate: moment().add(2, 'days').format('YYYY-MM-DD'),
  });
  const [thumbnailImg, setThumbnailImg] = useState('');
  const { fetchPlans, fetchQuizSeriesDetails, seriesDetails, fetchQuizSeriesList } = useFetchData();
  const { departmentData } = useFetchDepartments(seriesId);
  useEffect(() => {
    if (seriesId === null) {
      const roleIds = departmentData?.flatMap((dept) => dept?.roles?.map((role) => role.id));
      setData((prevData) => ({ ...prevData, departmentRoleIds: roleIds }));
    } else {
      fetchQuizSeriesDetails(seriesId);
      if (departmentData) {
        setData((prev) => ({ ...prev, departmentRoleIds: getSelectedRoleIds(departmentData) || [] }));
      }
    }
  }, [departmentData, fetchQuizSeriesDetails, seriesId]);

  useEffect(() => {
    if (seriesDetails && planList && seriesId) {
      const selectedPlans = seriesDetails.plans
        ?.map((planId) => planList.find((plan) => plan.value === planId))
        .filter(Boolean);

      setData((prev) => ({
        departmentRoleIds: [...prev.departmentRoleIds],
        ...seriesDetails,
        partners: seriesDetails?.rockedNetworkPartnerId,
        plans: selectedPlans || [],
        visibility: seriesDetails?.visibility || ['DEALERSHIP'],
      }));
    }
  }, [seriesDetails, planList, seriesId]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleData = ({ target: { name, value, checked } }) => {
    if (name === 'departmentRoleIds') {
      setData((prevData) => updateArrayState(prevData, name, value, checked));
    } else if (name === 'visibility') {
      const updatedVisibility = checked
        ? [...(data.visibility || []), value]
        : (data.visibility || []).filter((item) => item !== value);
      setData((prevData) => ({ ...prevData, visibility: updatedVisibility }));
    } else if (name === 'courseCode' && value.length <= 10) {
      setData((prev) => ({ ...prev, [name]: value }));
    } else if (name !== 'courseCode') {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (label, date) => setData((prev) => ({ ...prev, [label]: date }));

  const handleMultiSelect = (arr) => setData((prev) => ({ ...prev, plans: arr }));

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    setData((prev) => ({ ...prev, thumbnail: file }));
    const reader = new FileReader();
    reader.onload = (ev) => {
      setThumbnailImg(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDeptChange = (deptId, selected, departments) => {
    setData((prevData) => {
      const selectedDept = departments.find((dept) => dept.id === deptId);
      const roleIds = selectedDept?.roles.map((role) => role.id) || [];
      return {
        ...prevData,
        departmentRoleIds: selected
          ? [...new Set([...prevData.departmentRoleIds, ...roleIds])]
          : prevData.departmentRoleIds.filter((id) => !roleIds.includes(id)),
      };
    });
  };
  const backBtn = () => setStep(1);
  const handleSave = async () => {
    const check = validateSeries(data);
    if (check) {
      return;
    }
    if (step === 1) {
      setStep(2);
    } else {
      const payload = convertToFormData(data);
      dispatch(showProgress(true));
      try {
        const apiEndpoint =
          seriesId === null
            ? apiEndpoints.QUIZ_SERIES_CREATE
            : apiEndpoints.QUIZ_SERIES_UPDATE?.replace('ID', seriesId);
        const res = await service.post(apiEndpoint, payload);
        notifySuccess(`Quiz Series ${seriesId === null ? 'Added' : 'Updated'} Successfully`);
        fetchQuizSeriesList(res?.id, false);
        dispatch(showProgress(false));
        handleClose();
      } catch (error) {
        dispatch(showProgress(false));
        ErrorToast(error);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyle}>
        <CustomPopupTopBar add={seriesId === null} title="Quiz Series" close={handleClose} />
        <Grid container direction="column" className={classes.contentContainer}>
          <Grid item marginBottom="20px">
            <Steps step={step} stepInfo={['Series Info', 'Role Configurations']} />
          </Grid>
          {step === 1 && (
            <Grid item>
              <SeriesInfo
                data={data}
                handleData={handleData}
                handleDateChange={handleDateChange}
                handleMultiSelect={handleMultiSelect}
                handleThumbnailUpload={handleThumbnailUpload}
                thumbnailImg={thumbnailImg}
                planList={planList}
              />
            </Grid>
          )}
          {step === 2 && (
            <Grid item style={{ height: '50vh', display: 'flex', flexDirection: 'column' }}>
              <RoleLevelConfig
                data={data}
                handleChange={handleData}
                departmentRoleIds={data.departmentRoleIds}
                handleDeptChange={handleDeptChange}
                departmentData={departmentData}
              />
            </Grid>
          )}
        </Grid>
        <div className={classes.buttonContainer}>
          <CustomPopupFooter
            add={!seriesId}
            backBtn={backBtn}
            onClose={handleClose}
            saveContest={handleSave}
            step={step}
            totalSteps={2}
          />
        </div>
        <ShowProgress />
      </Box>
    </Modal>
  );
};

export default AddSeries;
